import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from '@simplewebauthn/browser';
import api from '../lib/api.js';
import { clearPasskeyFlag, hasPasskeyFlag, setPasskeyFlag } from '../lib/passkeyFlag.js';
import { markUnlockedThisLoad } from '../lib/appLockState.js';

function supported() {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

// Annulation par l'utilisateur (feuille Face ID fermée) ou délai écoulé : pas une erreur.
function isCancel(err) {
  return err?.name === 'NotAllowedError' || err?.name === 'AbortError';
}

function guessDeviceLabel() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent ?? '' : '';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return undefined;
}

/**
 * register() / login() renvoient { status: 'ok' | 'cancelled' | 'error', error? } et ne lèvent jamais.
 */
export function usePasskey() {
  const queryClient = useQueryClient();
  const [enrolled, setEnrolled] = useState(() => hasPasskeyFlag());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const register = useCallback(async (deviceLabel) => {
    setBusy(true);
    setError(null);
    try {
      const { challenge_id, options } = await api.post('/auth/passkeys/register/options', {});
      const response = await startRegistration({ optionsJSON: options });
      const body = { challenge_id, response };
      const label = deviceLabel ?? guessDeviceLabel();
      if (label) body.device_label = label;
      await api.post('/auth/passkeys/register/verify', body);
      setPasskeyFlag();
      setEnrolled(true);
      queryClient.invalidateQueries({ queryKey: ['passkeys'] });
      return { status: 'ok' };
    } catch (err) {
      if (isCancel(err)) return { status: 'cancelled' };
      if (err?.name === 'InvalidStateError') {
        // Cet appareil possède déjà une passkey pour ce compte.
        setPasskeyFlag();
        setEnrolled(true);
        const message = 'Face ID est déjà activé sur cet appareil.';
        setError(message);
        return { status: 'error', error: message };
      }
      const message = err?.message ?? "Impossible d'activer Face ID.";
      setError(message);
      return { status: 'error', error: message };
    } finally {
      setBusy(false);
    }
  }, [queryClient]);

  const login = useCallback(async () => {
    setBusy(true);
    setError(null);
    let challengeId;
    try {
      const { challenge_id, options } = await api.post('/auth/passkeys/login/options', {});
      challengeId = challenge_id;
      const response = await startAuthentication({ optionsJSON: options });
      await api.post('/auth/passkeys/login/verify', { challenge_id: challengeId, response });
      setPasskeyFlag();
      setEnrolled(true);
      markUnlockedThisLoad();
      return { status: 'ok' };
    } catch (err) {
      if (isCancel(err)) return { status: 'cancelled' };
      if (err?.status === 401 && challengeId) {
        // Passkey inconnue du serveur (supprimée) : le flag est périmé.
        clearPasskeyFlag();
        setEnrolled(false);
      }
      const message = err?.message ?? 'Connexion Face ID impossible.';
      setError(message);
      return { status: 'error', error: message };
    } finally {
      setBusy(false);
    }
  }, []);

  const forget = useCallback(() => {
    clearPasskeyFlag();
    setEnrolled(false);
  }, []);

  return { isSupported: supported(), enrolled, busy, error, register, login, forget };
}
